import { Skeleton } from "@mui/material";
import { styled, useTheme } from "@mui/system";
import { LineData } from "lightweight-charts";
import { Link as RouterLink, useHistory } from "react-router-dom";
import InfoIcon from "src/icons/portfolio/InfoIcon";
import { useUserSlice } from "src/store/features/user.slice";
import { ChartType } from "src/utils/types";
import {
  BALANCE_DROPDOWN_TOOLTIP,
  BEST_PERFORMERS_TOOLTIP,
  ICompareOptions,
  ITimePeriodOption,
} from "../defaults";
import BalanceChart from "./BalanceChart";
import { trackEvent } from "src/Amplitude";
import BalanceButton from "./BalanceButton";
import { useAppDispatch } from "src/store/store";
import { setPendingVerificationModal } from "src/store/features/modals.slice";
import useVerificationNavigation from "src/components/common/useVerificationNavigation";

export const BalanceWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "24px",
  alignSelf: "stretch",

  [theme.breakpoints.down(720)]: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
}));

export const BalanceFunds = styled("div")(({ theme }) => ({
  display: "flex",
  width: "239px",
  flexDirection: "column",
  gap: "24px",
  alignItems: "flex-start",
  alignSelf: "stretch",

  [theme.breakpoints.down(720)]: {
    width: "100%",
    height: "154px",
  },
}));

export const BalanceHeading = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",

  [theme.breakpoints.down(720)]: {
    alignSelf: "stretch",
  },
}));

export const BalanceText = styled(RouterLink)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontSize: "14px",
  textDecoration: "none",
  color: "inherit",
  cursor: "pointer",

  "&:hover": {
    opacity: 0.7,
  },
});

export const BalanceTextAmount = styled("div")({
  fontWeight: 600,
  fontSize: "26px",
});

export const BalanceTextPercent = styled("div")({
  fontSize: "14px",
});

export const GreenPlus = styled("span")({
  color: "#0BEC9F",
});

export const RedMinus = styled("span")({
  color: "#EF4444",
});

export const BalanceButtons = styled("div")({
  display: "flex",
  alignItems: "flex-start",
  gap: "12px",
});

export const BestPerformer = styled("div")(({ theme }) => ({
  display: "flex",
  height: "165px",
  padding: "16px",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "12px",
  flex: "1 0 0",
  borderRadius: "10px",
  background: "#FFF",
  boxShadow: "0px 4px 20px 0px #F5F3EF",
  fontWeight: 500,

  [theme.breakpoints.down(720)]: {
    width: "100%",
  },
}));

export const BestPerformerHeader = styled("div")({
  display: "flex",
  alignItems: "center",
  alignSelf: "stretch",
  gap: "8px",
  fontSize: "14px",
});

export const BalanceTableWrapper = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "40px",
  width: "100%",
});

export const BalanceMessage = styled("div")({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "24px",
  flex: "1 0 0",
  alignSelf: "stretch",
  fontSize: "14px",
  fontWeight: 400,
});

export const BalanceMessageButton = styled("button")({
  display: "flex",
  height: "25px",
  padding: "4px 8px",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  borderRadius: "4px",
  background: "#FC4C4C",
  color: "#FFF",
  cursor: "pointer",
  textDecoration: "none",
  fontWeight: 600,
  border: "none",

  "&:hover": {
    opacity: 0.5,
  },

  "&:focus": {
    backgroundColor: "#FFF6F6",
    color: "#FC4C4C",
  },
});

export const BalanceChartWrapper = styled("div")({
  display: "flex",
  height: "130px",
  justifyContent: "center",
  alignItems: "center",
  alignSelf: "stretch",
  position: "relative",
  borderBottom: "1px solid #FC4C4C",
});

export const BalanceChartCompare = styled("div")({
  display: "flex",
  width: "71.833px",
  alignItems: "flex-start",
  gap: "3px",
  position: "absolute",
  top: 0,
  right: 0,
  cursor: "pointer",
});

export const BalanceChartTimeFilter = styled("div")({
  display: "flex",
  width: "255.223px",
  alignItems: "flex-start",
  position: "absolute",
  bottom: 0,
  left: 0,
});

interface BalanceChartTimeFilterOptionProps {
  selected: boolean;
}
export const BalanceChartTimeFilterOption = styled("div")<BalanceChartTimeFilterOptionProps>(
  ({ theme, selected }) => ({
    width: "36px",
    fontSize: "12px",
    flexShrink: 0,
    cursor: "pointer",

    color: selected ? "#FC4C4C" : "",
    textDecorationLine: selected ? "underline" : "none",

    "&:hover": {
      color: "#FC4C4C",
      textDecorationLine: "underline",
    },
  }),
);

interface BalanceDefaultProps {
  isLoading: boolean;
  isCompareActive: boolean;
  selectedTime: string;
  bitcoinData: LineData[];
  ethereumData: LineData[];
  holdingsData: ChartType[];
  compareOptions: ICompareOptions[];
  timeFilterOptions: string[];
  timePeriodOptions: ITimePeriodOption[];
  timePeriod: string;
  handleTimePeriod: (newOpt: ITimePeriodOption) => void;
  onCompareClick: () => void;
  onToggleCompareOptions: (optionName: string) => void;
  onCompareClear: () => void;
  OnTimeFilterClick: (time: string) => void;
}

const BalanceDefault = (props: BalanceDefaultProps) => {
  const {
    isLoading,
    bitcoinData,
    ethereumData,
    selectedTime,
    compareOptions,
    timeFilterOptions,
    OnTimeFilterClick,
  } = props;
  const { isUnverified } = useUserSlice();
  const theme = useTheme();
  const history = useHistory();
  const { handleVerification } = useVerificationNavigation();

  const message = isUnverified
    ? "Verify account to access this feature"
    : "No balance available, deposit to view";
  const messageButton = isUnverified ? "Verify Now" : "Deposit Now";

  const handleButtonClick = () => {
    // Track the event
    trackEvent(`Clicked ${messageButton} Button`, undefined, "BalanceDefault");
    handleVerification(() => history.push("/wallets/deposit"));
  };

  const handleAmplitudeTracking = () => {
    trackEvent(`Clicked My Balance Button`, undefined, "BalanceDefault");
  };

  if (isLoading) return <Skeleton variant="rounded" width={"100%"} height={"345px"} />;
  return (
    <>
      {/* BALANCE */}
      <BalanceWrapper>
        <BalanceFunds>
          {/* BALANCE HEADING */}
          <BalanceHeading>
            <BalanceText to="/wallets/deposit" onClick={handleAmplitudeTracking}>
              Your Balance
              <InfoIcon tooltip={BALANCE_DROPDOWN_TOOLTIP} />
            </BalanceText>
            <BalanceTextAmount id="user-balance-amount" translate="no">$0</BalanceTextAmount>
            <BalanceTextPercent>
              <GreenPlus>+</GreenPlus>/<RedMinus>-</RedMinus>
            </BalanceTextPercent>
          </BalanceHeading>

          {/* BALANCE DEPOSIT/TRADE BUTTONS */}
          <BalanceButtons>
            <BalanceButton type="deposit" />
            <BalanceButton type="trade" />
          </BalanceButtons>
        </BalanceFunds>

        {/* BEST PERFORMERS */}
        <BestPerformer>
          <BestPerformerHeader>
            Your Best Performers
            <InfoIcon tooltip={BEST_PERFORMERS_TOOLTIP} color={theme.palette.text.primary} />
          </BestPerformerHeader>

          {/* DEPOSIT NOW MESSAGE */}
          <BalanceTableWrapper id="default-best-performing-table">
            <BalanceMessage>
              {message}
              <BalanceMessageButton onClick={handleButtonClick}>
                {messageButton}
              </BalanceMessageButton>
            </BalanceMessage>
          </BalanceTableWrapper>
        </BestPerformer>
      </BalanceWrapper>

      {/* CHART */}
      <BalanceChartWrapper>
        <BalanceChart
          data={[]}
          bitcoinData={bitcoinData}
          ethereumData={ethereumData}
          compareOptions={compareOptions}
          timeFilterOptions={timeFilterOptions}
        />

        {/* TIME FILTER */}
        <BalanceChartTimeFilter>
          {timeFilterOptions.map((opt) => (
            <BalanceChartTimeFilterOption
              key={opt}
              selected={selectedTime === opt}
              onClick={() => OnTimeFilterClick(opt)}>
              {opt}
            </BalanceChartTimeFilterOption>
          ))}
        </BalanceChartTimeFilter>
      </BalanceChartWrapper>
    </>
  );
};

export default BalanceDefault;
